import * as React from 'react'

import ChevronLeftIcon from './icon_chevron_left.svg?reactCompornt'
import { UseFormRegister } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { isMobileDisplay } from '@/utils/devise'

import * as Style from './style.css'
import CheckIcon from './icon_check.svg?reactCompornt'
import { TimePicker } from '~/atoms/search/TimePicker'

// アニメーションなしでも表示のイベントをハンドリングできるように
// 0秒の意味のないイベントを設定
const NO_ANIMATION_SETTING = {
  animate: { z: 0 },
  exit: { z: 0 },
  transition: { duration: 0 },
}

export interface IProps {
  register: UseFormRegister<ISearchRegularState>
  weeks: string[]
  prevHandler: () => void
  resetHandler: () => void
  submitHandler: () => void
  getValues: UseFormRegister<ISearchRegularState>
  regularSearch: ISearchRegularState
  setWeekHandler: (week: WeekType, value: number) => void
  setWorkTimeFromHandler: (week: WeekType, value: string) => void
  setWorkTimeFromUnselectedHandler: (week: WeekType, value: number) => void
  setWorkTimeToHandler: (week: WeekType, value: string) => void
  setWorkTimeToUnselectedHandler: (week: WeekType, value: number) => void
  disableButton: () => boolean
}

export const SearchConditionRegularDate = ({
  register,
  weeks,
  prevHandler,
  resetHandler,
  submitHandler,
  regularSearch,
  setWorkTimeFromHandler,
  setWorkTimeToHandler,
  disableButton,
}: IProps): JSX.Element => {
  const { t } = useTranslation()

  const buttonStyle = disableButton()
    ? Style.SearchButton
    : Style.SearchButtonDisabled

  return (
    <div className={Style.SearchConditionRegularDateWrapper}>
      <div className={Style.SearchConditionHeaderWrapper}>
        <div className={Style.SearchConditionRegularDateHeader}>
          定期予約
          <ChevronLeftIcon
            className={Style.SearchConditionRegularDateHeaderPrevIcon}
            onClick={prevHandler}
          />
          <div
            className={Style.SearchConditionRegularDateHeaderPrevName}
            onClick={prevHandler}
          ></div>
          <span
            className={Style.SearchConditionDateHeaderResetLink}
            onClick={resetHandler}
          >
            リセット
          </span>
        </div>
      </div>
      <div className={Style.SearchConditionRegularDate}>
        <div className={Style.SearchConditionFilterRow}>
          <div className={Style.SearchConditionFilterRowCell}>
            {weeks.map((week: WeekType) => (
              <div className={Style.SearchConditionWeeks} key={week}>
                <div className={Style.SearchConditionFilterWeek}>
                  <input
                    className={Style.SearchConditionFilterWeekCtrl}
                    type="checkbox"
                    {...register(`regular_search.${week}`)}
                    name={`regular_search[${week}]`}
                    id={`search${week.charAt(0).toUpperCase() + week.slice(1)}`}
                    defaultChecked={
                      regularSearch.regular_search[week] as unknown as boolean
                    }
                    value={1}
                  />
                  <label
                    htmlFor={`search${
                      week.charAt(0).toUpperCase() + week.slice(1)
                    }`}
                    className={Style.SearchConditionFilterWeekLabel}
                  >
                    <span className={Style.SearchConditionFilterWeekCheck}>
                      <CheckIcon
                        className={Style.SearchConditionFilterWeekCheckIcon}
                      />
                    </span>
                    {t(`search:params.regularSearchWeek.${week}`)}
                  </label>
                </div>
                <ul className={Style.SearchTimeInput}>
                  <li className={Style.SearchTimeSelecter}>
                    <label className={Style.SearchTimeSelecterLabel}>
                      開始
                    </label>
                    <div>
                      {(() => {
                        const timeFrom =
                          regularSearch.regular_search[`${week}_work_time_from`]
                        return (
                          <TimePicker
                            label={timeFrom}
                            defaultHour={timeFrom.slice(0, 2)}
                            defaultMinute={timeFrom.slice(3, 5)}
                            setTimeHandler={(value) =>
                              setWorkTimeFromHandler(week, value)
                            }
                          />
                        )
                      })()}
                    </div>

                    <div className={Style.SearchConditionFilterDay}>
                      <input
                        type="checkbox"
                        className={Style.SearchConditionFilterDayCtrl}
                        {...register(
                          `regular_search.${week}_work_time_from_unselected`,
                        )}
                        name={`regular_search[${week}_work_time_from_unselected]`}
                        id={`search${
                          week.charAt(0).toUpperCase() +
                          week.slice(1) +
                          'WorkTimeFromUnselected'
                        }`}
                        defaultChecked={
                          regularSearch.regular_search[
                            `${week}_work_time_from_unselected`
                          ] as unknown as boolean
                        }
                        value={1}
                      />
                      <label
                        htmlFor={`search${
                          week.charAt(0).toUpperCase() +
                          week.slice(1) +
                          'WorkTimeFromUnselected'
                        }`}
                        className={Style.SearchConditionFilterDayLabel}
                      >
                        <span className={Style.SearchConditionFilterDayCheck}>
                          <CheckIcon
                            className={Style.SearchConditionFilterDayCheckIcon}
                          />
                        </span>
                        指定無し
                      </label>
                    </div>
                  </li>
                  <li className={Style.SearchTimeBetweenLabel}>～</li>
                  <li className={Style.SearchTimeSelecter}>
                    <label className={Style.SearchTimeSelecterLabel}>
                      終了
                    </label>
                    <div>
                      {(() => {
                        const timeTo =
                          regularSearch.regular_search[`${week}_work_time_to`]
                        return (
                          <TimePicker
                            label={timeTo}
                            defaultHour={timeTo.slice(0, 2)}
                            defaultMinute={timeTo.slice(3, 5)}
                            setTimeHandler={(value) =>
                              setWorkTimeToHandler(week, value)
                            }
                          />
                        )
                      })()}
                    </div>

                    <div className={Style.SearchConditionFilterDay}>
                      <input
                        type="checkbox"
                        className={Style.SearchConditionFilterDayCtrl}
                        {...register(
                          `regular_search.${week}_work_time_to_unselected`,
                        )}
                        name={`regular_search[${week}_work_time_to_unselected]`}
                        id={`search${
                          week.charAt(0).toUpperCase() +
                          week.slice(1) +
                          'WorkTimeToUnselected'
                        }`}
                        defaultChecked={
                          regularSearch.regular_search[
                            `${week}_work_time_to_unselected`
                          ] as unknown as boolean
                        }
                        value={1}
                      />
                      <label
                        htmlFor={`search${
                          week.charAt(0).toUpperCase() +
                          week.slice(1) +
                          'WorkTimeToUnselected'
                        }`}
                        className={Style.SearchConditionFilterDayLabel}
                      >
                        <span className={Style.SearchConditionFilterDayCheck}>
                          <CheckIcon
                            className={Style.SearchConditionFilterDayCheckIcon}
                          />
                        </span>
                        指定無し
                      </label>
                    </div>
                  </li>
                </ul>
                {regularSearch.regular_search[`${week}`] == 1 &&
                  regularSearch.regular_search[`${week}_work_time_from`] >=
                    regularSearch.regular_search[`${week}_work_time_to`] &&
                  !regularSearch.regular_search[
                    `${week}_work_time_from_unselected`
                  ] &&
                  !regularSearch.regular_search[
                    `${week}_work_time_to_unselected`
                  ] && (
                    <div className={Style.DateErrorMessage}>
                      開始時間と終了時間を正しく指定してください
                    </div>
                  )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <AnimatePresence>
        <motion.div
          className={Style.FixedSearchButtonContainer}
          {...NO_ANIMATION_SETTING}
        >
          <span className={buttonStyle} onClick={submitHandler}>
            決定
          </span>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

export default SearchConditionRegularDate
